import React, { useEffect, useState } from "react";

import { Box, Button, Container, Grid, Link, Typography } from "@mui/material";

import {
  findGetParameter,
  getData,
  postData,
} from "../../components/utilities/UtilityFunctions";
import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import GenericPicker from "../../components/utilities/GenericPicker";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  BCPArrowLeft,
  BCPFloppyDisk,
} from "../../components/icons/bcp/BCPIcons";

export default function SquareDetails() {
  const [processing, setProcessing] = useState(true);
  const [locations, setLocations] = useState([]);
  const [gameStore, setGameStore] = useState(null);

  const gameStoreId = findGetParameter("orgId");

  useEffect(() => {
    const go = async () => {
      try {
        if (!gameStoreId) {
          throw new Error("Missing required url parameters");
        }

        const apiGameStore = await getData(`/gamestores/${gameStoreId}`, {
          "expand[]": "bankAccount",
        });

        setGameStore(apiGameStore);

        // Get locations for the Square account
        if (apiGameStore?.bankAccount?.id && gameStoreId) {
          const apiLocations = await getData(
            `/bankaccounts/square/${apiGameStore.bankAccount.id}/locations`,
            {
              objectId: gameStoreId,
              accountType: "gameStore",
            }
          );

          if (!apiLocations || apiLocations?.locations?.length === 0) {
            throw new Error("No locations found for the Square account");
          } else {
            // Format locations for the picker component
            const formattedLocations = apiLocations.locations.map((loc) => ({
              label: `${loc?.address?.address_line_1 ? loc.address.address_line_1 + ", " : ""}${loc?.address?.locality ? loc.address.locality + ", " : ""}${loc?.address?.administrative_district_level_1 ? loc.address.administrative_district_level_1 + ", " : ""}${loc?.address?.postal_code ? loc.address.postal_code : ""}${loc?.address?.country ? " (" + loc.address.country + ")" : ""}`,
              value: loc.id,
            }));
            setLocations(formattedLocations);
          }
        } else {
          throw new Error("Error loading Square account");
        }
      } catch (err) {
        postNotification("error", err);
      } finally {
        setProcessing(false);
      }
    };
    go();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Container sx={{ p: 2, pt: 2 }} maxWidth="lg">
          <Grid container sx={{ p: 2, gap: 2 }}>
            <Grid item xs={12} sx={{ mb: 2 }}>
              <Link
                href={`/organize/organization/${gameStoreId}?active_tab=banking`}
                color="link.main"
                sx={{
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                <BCPArrowLeft
                  sx={{ "padding-right": "1rem", "padding-bottom": "2px" }}
                />{" "}
                Back to {`${gameStore?.name || "Game Store"}`}
              </Link>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h3" fontWeight="700" marginBottom={2}>
                {gameStore?.bankAccount?.label}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="h4">Update Location</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Formik
                    initialValues={{
                      location: gameStore?.bankAccount?.locationId || "",
                    }}
                    enableReinitialize={true}
                    validationSchema={Yup.object().shape({
                      location: Yup.string().required(),
                    })}
                    onSubmit={async (values) => {
                      try {
                        setProcessing(true);

                        if (!gameStore?.bankAccount?.id) {
                          throw new Error("No Square account found");
                        }

                        const response = await postData(
                          `/bankaccounts/square/${gameStore.bankAccount.id}/updatelocation`,
                          {
                            locationId: values.location,
                            accountId: gameStore.bankAccount.id,
                          }
                        );

                        if (
                          response?.message?.toLowerCase()?.includes("success")
                        ) {
                          postNotification(
                            "success",
                            "Location updated successfully!"
                          );
                        } else {
                          throw new Error("Failed to update Square location");
                        }
                      } catch (err) {
                        postNotification("error", err);
                      } finally {
                        setProcessing(false);
                      }
                    }}
                  >
                    {({ values, handleChange, handleSubmit }) => (
                      <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            {locations?.length ? (
                              <GenericPicker
                                name="location"
                                label="Select Location"
                                placeholder="Select a location"
                                value={values.location}
                                handleChange={handleChange}
                                list={locations}
                                required
                                inclAny={false}
                                fullWidth
                              />
                            ) : (
                              <GenericPicker
                                label="Select Location"
                                placeholder="Loading locations..."
                                value={values.location}
                                handleChange={handleChange}
                                list={[]}
                                required
                                fullWidth
                                disabled
                              />
                            )}
                          </Grid>

                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              color="primary"
                              type="submit"
                              fullWidth
                              startIcon={<BCPFloppyDisk />}
                              disabled={!Boolean(gameStore)}
                            >
                              Save Changes
                            </Button>
                          </Grid>
                        </Grid>
                      </form>
                    )}
                  </Formik>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {processing && <ModalLoadingFull />}
    </>
  );
}
